import { Controller } from "@hotwired/stimulus"
import Map from "ol/Map"
import View from "ol/View"
import Feature from "ol/Feature"
import Point from "ol/geom/Point"
import CircleGeom from "ol/geom/Circle"
import TileLayer from "ol/layer/Tile"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import OSM from "ol/source/OSM"
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS"
import WMTSCapabilities from "ol/format/WMTSCapabilities"
import { defaults as defaultControls } from "ol/control/defaults"
import { fromLonLat } from "ol/proj"
import { DEVICE_PIXEL_RATIO } from "ol/has"
import Fill from "ol/style/Fill"
import Stroke from "ol/style/Stroke"
import CircleStyle from "ol/style/Circle"
import Style from "ol/style/Style"

export class LookupMapController extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    accuracy: Number
  }

  async connect() {
    if (!this.hasLatitudeValue || !this.hasLongitudeValue) return

    this.boundRefreshSize = this.refreshSize.bind(this)
    this.map = new Map({
      target: this.element,
      controls: defaultControls({
        rotate: false,
        attributionOptions: {
          collapsible: false,
          collapsed: false,
          tipLabel: "Map attribution"
        }
      }),
      view: new View({
        center: fromLonLat([this.longitudeValue, this.latitudeValue]),
        zoom: this.zoomLevel(),
        enableRotation: false,
        maxZoom: 21
      })
    })

    this.setupFallbackBaseMap()
    this.addLocationMarker()
    this.installResizeObserver()
    this.refreshSize()
    void this.upgradeBaseMap()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null

    if (this.sizeUpdateFrame) {
      cancelAnimationFrame(this.sizeUpdateFrame)
      this.sizeUpdateFrame = null
    }

    if (!this.map) return

    this.map.setTarget(null)
    this.map = null
  }

  setupFallbackBaseMap() {
    this.baseLayer = new TileLayer({
      source: new OSM(),
      zIndex: 0
    })

    this.map.addLayer(this.baseLayer)
  }

  async upgradeBaseMap() {
    try {
      await this.setupBaseMap()
      this.refreshSize()
    } catch (error) {
      console.warn("Failed to load preferred basemap, leaving fallback map in place.", error)
    }
  }

  async setupBaseMap() {
    const capabilities = await this.fetchCapabilities()
    const hiDPI = DEVICE_PIXEL_RATIO > 1
    const layerName = hiDPI ? "bmaphidpi" : "geolandbasemap"
    const options = optionsFromCapabilities(capabilities, {
      layer: layerName,
      matrixSet: "google3857",
      style: "normal"
    })

    options.tilePixelRatio = hiDPI ? 2 : 1
    options.attributions = "Data source: basemap.at"
    options.attributionsCollapsible = false

    this.baseLayer.setSource(new WMTS(options))
  }

  async fetchCapabilities() {
    if (this.capabilities) return this.capabilities

    const response = await fetch("https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml")
    const text = await response.text()
    this.capabilities = new WMTSCapabilities().read(text)
    return this.capabilities
  }

  installResizeObserver() {
    if (typeof ResizeObserver === "undefined") return

    this.resizeObserver = new ResizeObserver(this.boundRefreshSize)
    this.resizeObserver.observe(this.element)
  }

  refreshSize() {
    if (!this.map) return

    if (this.sizeUpdateFrame) cancelAnimationFrame(this.sizeUpdateFrame)

    this.sizeUpdateFrame = requestAnimationFrame(() => {
      if (!this.map) return

      this.map.updateSize()
      this.map.renderSync()
      this.sizeUpdateFrame = null
    })
  }

  addLocationMarker() {
    const point = new Point(fromLonLat([this.longitudeValue, this.latitudeValue]))

    const feature = new Feature({ geometry: point })
    feature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: "#1f6feb" }),
          stroke: new Stroke({ color: "#ffffff", width: 3 })
        })
      })
    )

    this.map.addLayer(
      new VectorLayer({
        source: new VectorSource({ features: [feature] }),
        zIndex: 20
      })
    )

    if (!this.hasAccuracyValue || this.accuracyValue <= 0) return

    const accuracyFeature = new Feature({
      geometry: new CircleGeom(fromLonLat([this.longitudeValue, this.latitudeValue]), this.accuracyValue)
    })

    accuracyFeature.setStyle(
      new Style({
        stroke: new Stroke({ color: "rgba(31, 111, 235, 0.55)", width: 2 }),
        fill: new Fill({ color: "rgba(31, 111, 235, 0.14)" })
      })
    )

    this.map.addLayer(
      new VectorLayer({
        source: new VectorSource({ features: [accuracyFeature] }),
        zIndex: 10
      })
    )
  }

  zoomLevel() {
    if (!this.hasAccuracyValue) return 16
    if (this.accuracyValue > 5000) return 10
    if (this.accuracyValue > 1000) return 12
    if (this.accuracyValue > 250) return 14

    return 16
  }
}
